import { env } from '@/config/env';

// Types
export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'none';

export type LogContext = {
  component?: string;
  action?: string;
  userId?: string;
  metadata?: Record<string, unknown>;
};

export type LogEntry = {
  level: LogLevel;
  message: string;
  timestamp: string;
  context?: LogContext;
  data?: unknown;
};

export type LoggerConfig = {
  level: LogLevel;
  prefix?: string;
  enableTimestamp?: boolean;
  enableColors?: boolean;
};

// Log level priority mapping
const LOG_LEVELS: Record<LogLevel, number> = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
  none: 4,
};

// Console colors for different log levels
const LOG_COLORS: Record<LogLevel | 'reset', string> = {
  debug: '\x1b[36m', // Cyan
  info: '\x1b[32m', // Green
  warn: '\x1b[33m', // Yellow
  error: '\x1b[31m', // Red
  none: '', // No color for none
  reset: '\x1b[0m',
};

class Logger {
  config: LoggerConfig; // Made public for test utilities

  constructor(config?: Partial<LoggerConfig>) {
    // Check if we're in test environment (Vitest sets NODE_ENV=test)
    const isTest = typeof process !== 'undefined' && process.env.NODE_ENV === 'test';
    const debugLogs = typeof process !== 'undefined' && process.env.DEBUG_LOGS === 'true';

    // Default log level: none in tests (unless DEBUG_LOGS=true), otherwise from env or 'info'
    const defaultLevel = isTest && !debugLogs ? 'none' : env.VITE_LOG_LEVEL || 'info';

    this.config = {
      level: defaultLevel,
      prefix: '',
      enableTimestamp: true,
      enableColors: env.VITE_APP_ENV === 'development',
      ...config,
    };
  }

  private shouldLog(level: LogLevel): boolean {
    if (this.config.level === 'none') return false;
    return LOG_LEVELS[level] >= LOG_LEVELS[this.config.level];
  }

  private formatMessage(level: LogLevel, message: string, context?: LogContext): string {
    const parts: string[] = [];

    // Add timestamp if enabled
    if (this.config.enableTimestamp) {
      parts.push(`[${new Date().toISOString()}]`);
    }

    // Add log level
    parts.push(`[${level.toUpperCase()}]`);

    // Add prefix if provided
    if (this.config.prefix) {
      parts.push(`[${this.config.prefix}]`);
    }

    // Add context if provided
    if (context?.component) {
      parts.push(`[${context.component}]`);
    }
    if (context?.action) {
      parts.push(`[${context.action}]`);
    }

    // Add message
    parts.push(message);

    return parts.join(' ');
  }

  private logToConsole(
    level: LogLevel,
    formattedMessage: string,
    data?: unknown,
    context?: LogContext,
  ): void {
    // Map log level to console method
    let consoleMethod: 'log' | 'info' | 'warn' | 'error';
    switch (level) {
      case 'debug':
        consoleMethod = 'log';
        break;
      case 'info':
        consoleMethod = 'info';
        break;
      case 'warn':
        consoleMethod = 'warn';
        break;
      case 'error':
        consoleMethod = 'error';
        break;
      default:
        // Should never reach here due to shouldLog check, but handle 'none'
        return;
    }

    const color = this.config.enableColors ? LOG_COLORS[level] : '';
    const reset = this.config.enableColors ? LOG_COLORS.reset : '';

    console[consoleMethod](
      `${color}${formattedMessage}${reset}`,
      ...(data !== undefined ? ['\n', data] : []),
      ...(context?.metadata ? ['\nMetadata:', context.metadata] : []),
    );
  }

  private log(level: LogLevel, message: string, data?: unknown, context?: LogContext): void {
    if (!this.shouldLog(level)) return;

    const formattedMessage = this.formatMessage(level, message, context);
    this.logToConsole(level, formattedMessage, data, context);
  }

  debug(message: string, data?: unknown, context?: LogContext): void {
    this.log('debug', message, data, context);
  }

  info(message: string, data?: unknown, context?: LogContext): void {
    this.log('info', message, data, context);
  }

  warn(message: string, data?: unknown, context?: LogContext): void {
    this.log('warn', message, data, context);
  }

  error(message: string, error?: unknown, context?: LogContext): void {
    // Special handling for error objects
    if (error instanceof Error) {
      const errorData = {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
      this.log('error', message, errorData, context);
    } else {
      this.log('error', message, error, context);
    }
  }

  // Create a child logger with additional context
  child(additionalConfig: Partial<LoggerConfig>): Logger {
    return new Logger({
      ...this.config,
      ...additionalConfig,
      prefix: [this.config.prefix, additionalConfig.prefix].filter(Boolean).join(':'),
    });
  }

  // Group related logs together
  group(label: string, fn: () => void): void {
    if (!this.shouldLog('debug')) {
      fn();
      return;
    }

    console.group(label);
    try {
      fn();
    } finally {
      console.groupEnd();
    }
  }

  // Time a function execution
  async time<T>(label: string, fn: () => T | Promise<T>): Promise<T> {
    if (!this.shouldLog('debug')) {
      return await fn();
    }

    const start = performance.now();
    try {
      const result = await fn();
      const duration = performance.now() - start;
      this.debug(`${label} completed`, { duration: `${duration.toFixed(2)}ms` });
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      this.error(`${label} failed`, error, { metadata: { duration: `${duration.toFixed(2)}ms` } });
      throw error;
    }
  }

  // Assert a condition and log an error if it fails
  assert(condition: boolean, message: string, data?: unknown): void {
    if (!condition) {
      this.error(`Assertion failed: ${message}`, data);
    }
  }

  // Create a table log (useful for debugging arrays/objects)
  table(data: unknown, columns?: string[]): void {
    if (!this.shouldLog('debug')) return;

    console.table(data, columns);
  }
}

export const logger = new Logger();

export const createLogger = (config?: Partial<LoggerConfig>): Logger => {
  return new Logger(config);
};
